/**
 * Auth Module
 * Authentication helpers and state management
 */

import { showSignInReason } from "../link-join.js";
import { emit, Events } from '../../../../engine/core/events.js';
import { t } from '../../../../shared/i18n/index.js';

// ============================================
// State
// ============================================

let _currentUser = null;
let _masterKey = null;
let _e2eeEnvelope = null;

// Dependency: API function (injected at runtime)
let _api = null;

/**
 * Initialize auth module with dependencies
 * @param {Object} deps - Dependencies
 * @param {Function} deps.api - API helper function
 */
export function initAuth(deps = {}) {
    if (deps.api) _api = deps.api;
}

// ============================================
// Getters/Setters for Auth State
// ============================================

export function getCurrentUser() {
    return _currentUser;
}

export function setCurrentUser(user) {
    _currentUser = user;
    if (user) {
        emit(Events.AUTH_LOGIN, { user });
    }
}

export function getMasterKey() {
    return _masterKey;
}

export function setMasterKey(key) {
    // Zero the previous key material before replacing (defense-in-depth)
    if (_masterKey instanceof Uint8Array) _masterKey.fill(0);
    _masterKey = key;
}

export function getE2eeEnvelope() {
    return _e2eeEnvelope;
}

export function setE2eeEnvelope(envelope) {
    _e2eeEnvelope = envelope;
}

export function isLoggedIn() {
    return _currentUser !== null;
}

export function hasEncryptionKey() {
    return _masterKey !== null;
}

// ============================================
// Password Validation
// ============================================

/**
 * Validate password against security requirements
 * @param {string} password - Password to validate
 * @returns {string[]} Array of error messages (empty if valid)
 */
export function validatePassword(password) {
    const errors = [];

    if (password.length < 8) {
        errors.push(t("account.password.minLength"));
    }
    if (!/[0-9]/.test(password)) {
        errors.push(t("account.password.number"));
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        errors.push(t("account.password.special"));
    }
    if (!/[a-z]/.test(password)) {
        errors.push(t("account.password.lowercase"));
    }
    if (!/[A-Z]/.test(password)) {
        errors.push(t("account.password.uppercase"));
    }

    return errors;
}

/**
 * Check if password meets minimum requirements
 * @param {string} password - Password to check
 * @returns {boolean} True if valid
 */
export function isValidPassword(password) {
    return validatePassword(password).length === 0;
}

// ============================================
// UI Helpers
// ============================================

/**
 * Show auth error message
 * @param {string} message - Error message
 * @param {string} errorId - Error element ID
 */
export function showAuthError(message, errorId = "login-error") {
    const el = document.getElementById(errorId);
    if (el) {
        el.textContent = message;
        el.classList.remove("hidden");
    }
}

/**
 * Hide auth error message
 * @param {string} errorId - Error element ID
 */
export function hideAuthError(errorId = "login-error") {
    const el = document.getElementById(errorId);
    if (el) {
        el.classList.add("hidden");
    }
}

/**
 * Show password error message
 * @param {string} message - Error message
 */
export function showPasswordError(message) {
    const el = document.getElementById("password-error");
    if (el) {
        el.textContent = message;
        el.classList.remove("hidden");
    }
}

// ============================================
// Screen Management
// ============================================

const screens = [
    "login-screen",
    "signup-screen",
    "profile-screen",
    "main-app",
    "recover-screen"
];

/**
 * Show a specific auth screen, hide others
 * @param {string} screenId - Screen to show
 */
export function showScreen(screenId) {
    screens.forEach(id => {
        const el = document.getElementById(id);
        if (el) {
            el.classList.toggle("hidden", id !== screenId);
        }
    });
    // Signing in: the keyboard goes to the username at once (Alex,
    // 2026-10-27: "login page: directly focus keyboard on username").
    if (screenId === "login-screen") document.getElementById("login-username")?.focus();
    // A map's link is waiting: these screens say why the person is here.
    if (screenId === "login-screen" || screenId === "signup-screen") showSignInReason(screenId);
    // Another interface takes the main screen's place (interfaces/index.js).
    emit(Events.SCREEN_SHOWN, { screenId });
}

/**
 * Get currently visible screen
 * @returns {string|null} Screen ID or null
 */
export function getCurrentScreen() {
    for (const id of screens) {
        const el = document.getElementById(id);
        if (el && !el.classList.contains("hidden")) {
            return id;
        }
    }
    return null;
}

// ============================================
// Login Button State
// ============================================

/**
 * Update login/logout button state
 * @param {Object} options - Options
 * @param {Function} options.onLoginClick - Login button click handler
 * @param {Function} options.onProfileClick - Profile button click handler
 */
export function updateLoginButton(options = {}) {
    const loginBtn = document.getElementById("login-btn");
    const logoutBtn = document.getElementById("logout-btn");

    if (_currentUser) {
        if (loginBtn) {
            loginBtn.textContent = _currentUser.username;
            loginBtn.title = t("account.header.viewProfile");
            if (options.onProfileClick) {
                loginBtn.onclick = options.onProfileClick;
            }
        }
        if (logoutBtn) {
            logoutBtn.classList.remove("hidden");
        }
    } else {
        if (loginBtn) {
            loginBtn.textContent = t("auth.login.submit");
            loginBtn.title = t("account.header.signInTitle");
            if (options.onLoginClick) {
                loginBtn.onclick = options.onLoginClick;
            }
        }
        if (logoutBtn) {
            logoutBtn.classList.add("hidden");
        }
    }
}

// ============================================
// Clear Auth State
// ============================================

/**
 * Clear all auth state (for logout)
 */
export function clearAuthState() {
    const wasLoggedIn = _currentUser !== null;

    _currentUser = null;
    // Zero key material before releasing reference (defense-in-depth)
    if (_masterKey instanceof Uint8Array) _masterKey.fill(0);
    _masterKey = null;
    _e2eeEnvelope = null;

    if (wasLoggedIn) {
        emit(Events.AUTH_LOGOUT, {});
    }
}

// ============================================
// Session Check
// ============================================

/**
 * Check if user has an active session
 * @returns {Promise<Object|null>} User object or null
 */
export async function checkSession() {
    if (!_api) {
        console.warn("[Auth] API not initialized");
        return null;
    }

    try {
        const user = await _api("/api/auth/me");
        setCurrentUser(user);
        return user;
    } catch (err) {
        // Not logged in or session expired
        return null;
    }
}
